import NewUserDashboardView from "./views/NewUserDashboardView.vue";
import LoginView from "./views/LoginView.vue";
import LoggedOutView from "./views/LoggedOutView.vue";
import RedirectingView from "./views/RedirectingView.vue";
import ServerConnectView from "./views/ServerConnectView.vue";
import TwoFactorView from "./views/TwoFactorView.vue";
import TwoFactorOnboardView from "./views/TwoFactorOnboardView.vue";
import ForceUpdateView from "./views/ForceUpdateView.vue";
import SessionLimitView from "./views/SessionLimitView.vue";

import globals from "./globals";
import api from "./services/api";
import { createRouter, createWebHistory, RouteLocationNormalizedGeneric, RouteRecordRaw } from "vue-router";
import { useAuthStore } from "./pinia/auth.ts";
import { isNativePlatform } from "./capacitor/platform";
import { getServerUrl } from "./capacitor/server-url";
import { isForceUpdateActive } from "./capacitor/force-update-state";

declare module "vue-router" {
  interface RouteMeta {
    title?: string;
    favicon?: string;
    requiresUserLogin?: boolean;
    requiresAdminLogin?: boolean;
    rejectsCloudMode?: boolean;
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: "/home",
    name: "Home",
    component: NewUserDashboardView,
    meta: { title: "Dashboard - Knocknoc", requiresUserLogin: true },
  },
  {
    path: "/home/new",
    redirect: "/home",
  },
  {
    path: "/otp/verify",
    name: "TwoFactor",
    component: TwoFactorView,
    meta: { title: "Authenticate - Knocknoc" },
  },
  {
    path: "/otp/onboard",
    name: "TwoFactorOnboard",
    component: TwoFactorOnboardView,
    meta: { title: "Authenticate - Knocknoc" },
  },
  {
    path: "/otp/setup",
    name: "TwoFactorSetup",
    component: TwoFactorOnboardView,
    props: { fromKnoc: true },
    meta: { title: "Set up 2FA - Knocknoc", requiresUserLogin: true },
  },
  {
    path: "/session-limit",
    name: "SessionLimit",
    component: SessionLimitView,
    meta: { title: "Session limit - Knocknoc", requiresUserLogin: true },
  },
  {
    path: "/connect",
    name: "ServerConnect",
    component: ServerConnectView,
    meta: { title: "Connect - Knocknoc" },
  },
  {
    path: "/update-required",
    name: "ForceUpdate",
    component: ForceUpdateView,
    meta: { title: "Update required - Knocknoc" },
  },
  {
    path: "/",
    name: "Login",
    component: LoginView,
    meta: { title: "Login - Knocknoc" },
  },
  {
    path: "/logged-out",
    name: "LoggedOut",
    component: LoggedOutView,
    meta: { title: "Logged out - Knocknoc" },
  },
  {
    path: "/redirecting",
    name: "Redirecting",
    component: RedirectingView,
    meta: { title: "Redirecting - Knocknoc" },
  },
  { path: "/:pathMatch(.*)*", redirect: "/" },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    // Same-page query/hash updates (e.g. filter and pagination URL sync) should
    // leave the scroll position alone rather than jump to the top.
    if (to.path === from.path) {
      return false;
    }
    return { top: 0 };
  },
});

function handleRouteMetadata(to: RouteLocationNormalizedGeneric) {
  const toRouteChain = to.matched;
  let title = "Knocknoc";

  // If the route doesn't have a title, get it from the closest parent
  for (let i = toRouteChain.length - 1; i >= 0; i--) {
    const route = toRouteChain[i];
    if (route.meta?.title) {
      title = route.meta.title;
      break;
    }
  }

  document.title = title;

  let favicon = "/favicon.ico";

  // If the route doesn't have a favicon, get it from the closest parent
  for (let i = toRouteChain.length - 1; i >= 0; i--) {
    const route = toRouteChain[i];
    if (route.meta?.favicon) {
      favicon = route.meta.favicon;
      break;
    }
  }

  let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = favicon;
}

router.beforeEach(async (to, _, next) => {
  handleRouteMetadata(to);

  // Force-update wall: once the app-updater has observed that the server
  // requires a newer native app version than this APK/IPA provides, every
  // route lands on the ForceUpdate screen until the app is updated.
  if (isForceUpdateActive()) {
    if (to.name !== "ForceUpdate") {
      next({ name: "ForceUpdate" });
      return;
    }
    next();
    return;
  }

  // On native, require a server URL before allowing any route except /connect
  if (isNativePlatform()) {
    if (to.name !== "ServerConnect") {
      const serverUrl = await getServerUrl();
      if (!serverUrl) {
        next({ name: "ServerConnect" });
        return;
      }
    }
  } else if (to.name === "ServerConnect" || to.name === "ForceUpdate") {
    // Web users should never see native-only screens
    next({ name: "Login" });
    return;
  }

  const authStore = useAuthStore();

  let user = null;

  try {
    if (authStore.hasFreshUser()) {
      user = authStore.user;
    } else if (authStore.user != null) {
      user = await authStore.fetchStatus();
    } else if (to.meta?.requiresUserLogin) {
      // Fresh page load with empty sessionStorage (e.g. after
      // postLoginNavigate). The session cookie may still be valid,
      // so check before bouncing to login.
      user = await authStore.fetchStatus({ force: true });
    }
  } catch {
    user = null;
  }

  if (user != null) {
    if (to.name === "Login") {
      next({ name: "Home", params: to.params, query: to.query });
      return;
    }

    // A session without a session slot (login landed on a full pool) stays on
    // the session-limit screen until it frees a slot and continues.
    if (user.HasSessionSlot === false && to.meta?.requiresUserLogin && to.name !== "SessionLimit") {
      next({ name: "SessionLimit" });
      return;
    }
    if (user.HasSessionSlot !== false && to.name === "SessionLimit") {
      next({ name: "Home" });
      return;
    }
  }

  if (to.meta) {
    if (!user && to.meta.requiresUserLogin) {
      next({ name: "Login", params: to.params, query: to.query });
      return;
    }
  }

  // For all other cases, proceed with navigation
  next();
});

router.afterEach(() => {
  api.get("/system_status").then(
    (response) => {
      globals.checkJsBundleHash(response.data.JsBundleHash);
    },
    () => {
      return null;
    },
  );
});
